import { ref, onUnmounted, getCurrentInstance } from 'vue'
import Network from '@/Network'
import type {
  DSAttendanceDoctorSuggestion,
  DSAttendanceDoctorSuggestionField
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'

/**
 * RF-03/RN-05 do PRD 32 — a política de requisição do autocomplete de profissionais.
 *
 * O autocomplete é acelerador, nunca validador (RN-04): este composable só produz a
 * lista, jamais restringe o que o auditor digita. Por isso o erro de rede fecha a lista
 * em silêncio — um toast por célula em folha de 20 linhas seria ruído, e a digitação
 * livre segue valendo.
 *
 * O mínimo de caracteres também roda no servidor; o gating daqui existe para economizar
 * requisição, não para validar.
 */

export const SUGGESTION_DEBOUNCE_MS = 300

/** RF-03 — teto de 8 itens; o excedente vira o "+ N resultados". */
export const SUGGESTION_LIMIT = 8

/** RN-05 — 3 em célula de nome; 1 em CRM, para alcançar CRMs antigos de 1–2 dígitos. */
export const MIN_SEARCH_LENGTH: Record<DSAttendanceDoctorSuggestionField, number> = {
  crm: 1,
  name: 3
}

export function meetsMinimumLength(
  field: DSAttendanceDoctorSuggestionField,
  term: string
): boolean {
  return term.trim().length >= MIN_SEARCH_LENGTH[field]
}

export function useDoctorSuggestions(
  field: DSAttendanceDoctorSuggestionField,
  getContainerId: () => string | number
) {
  const suggestions = ref<DSAttendanceDoctorSuggestion[]>([])
  const overflowCount = ref(0)
  /** Uma busca concluída com sucesso — só então há lista (ou estado vazio) a exibir. */
  const hasResult = ref(false)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Corrida resolvida por última resposta ganha: o wrapper de request não aceita cancel
   * token no `get`, então a resposta obsoleta chega e é descartada por este contador.
   */
  let latestRequestId = 0

  function clearResults() {
    suggestions.value = []
    overflowCount.value = 0
    hasResult.value = false
  }

  function reset() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null
    latestRequestId += 1
    clearResults()
  }

  async function fetchSuggestions(term: string) {
    const requestId = (latestRequestId += 1)

    try {
      const response = await Network.dsAttendance.getDoctorSuggestions(getContainerId(), {
        field,
        search: term,
        limit: SUGGESTION_LIMIT
      })
      if (requestId !== latestRequestId) return

      suggestions.value = response.data
      overflowCount.value = Math.max(response.count - response.data.length, 0)
      hasResult.value = true
    } catch (error) {
      if (requestId !== latestRequestId) return
      // A UI segue silenciosa de propósito (ver o cabeçalho), mas o silêncio TOTAL de antes
      // custou quatro rondas de UAT: 403, 404 e "sem resultado" ficavam indistinguíveis, sem
      // nada em tela nem no console. O `console.warn` devolve diagnóstico a quem abre o
      // DevTools sem gerar ruído para o auditor — mesmo padrão do fallback de
      // `getRejectionReasons` em `DsAttendanceAwaitingAudit.vue`.
      console.warn('Não foi possível buscar sugestões de profissionais.', error)
      clearResults()
    }
  }

  function search(term: string) {
    if (debounceTimer) clearTimeout(debounceTimer)

    if (!meetsMinimumLength(field, term)) {
      reset()
      return
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void fetchSuggestions(term.trim())
    }, SUGGESTION_DEBOUNCE_MS)
  }

  if (getCurrentInstance()) {
    onUnmounted(reset)
  }

  return { suggestions, overflowCount, hasResult, search, reset }
}
